import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { approvalKeys } from '~/lib/query-keys';
import type { ApprovalEdit } from '~/features/chat/approval-edit-draft';

/**
 * FRONT-1639: `POST /api/approvals/:id/approve`'s response body (see
 * `src/agents/routes.ts` and `DeferredExecutionResult` in
 * `src/autonomy/deferred-execution.ts`).
 *
 * A LIVE-mode row's response is just `{ resolved: true }` — no `executed`
 * field at all, which is how the card distinguishes "today's shape,
 * unchanged" from a deferred approval that actually ran its tool call in
 * this same request (or is still running past the route's ~25s wait).
 */
export interface ApproveResponse {
  resolved?: boolean;
  executed?: 'succeeded' | 'failed' | 'refused' | 'running';
  /** Short label of what was saved — `executed: "succeeded"` only. */
  wrote?: string;
  /** The human sentence to show, on `succeeded`/`failed`/`refused`. */
  message?: string;
  /** Present on `failed`/`refused`. */
  error?: string;
  /** FRONT-1747: true when what was approved is the user's EDITED version. */
  revised?: boolean;
  /**
   * FRONT-3031: present only when the person chose "Yes, and don't ask again
   * this plan". True: the row is on the plan now. False: the call was
   * approved, but the plan had already ended, so nothing was added.
   */
  addedToPlan?: boolean;
}

/**
 * FRONT-1747: an approve either confirms the proposal as-is (just the id,
 * every existing caller) or carries the user's edit from the Quick OK card.
 */
export type ApproveInput =
  | string
  | { id: string; edit: ApprovalEdit }
  /** FRONT-3031: "Yes, and don't ask again this plan". */
  | { id: string; addToPlan: true };

// Refetch on `onSettled` (not `onSuccess`): the decision is written to the DB
// before the resolve returns, so even if the resolve call is slow or errors the
// list must still refresh and the card must leave the UI. Waiting for success
// is what let a hung resolve strand the card until a manual reload (FRONT-726).
export function useApprove() {
  const qc = useQueryClient();
  return useMutation({
    // FRONT-1639: the body is returned (not discarded) so the card can settle
    // on the server's real execution outcome for a deferred approval instead
    // of a local "Saved to your memory." constant that may not be true.
    mutationFn: (input: ApproveInput) => {
      if (typeof input === 'string') return api.post<ApproveResponse>(`/api/approvals/${input}/approve`);
      const body = 'addToPlan' in input ? { addToPlan: true } : { edit: input.edit };
      return api.post<ApproveResponse>(`/api/approvals/${input.id}/approve`, body);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: approvalKeys.all }),
  });
}

export function useReject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/approvals/${id}/reject`),
    onSettled: () => qc.invalidateQueries({ queryKey: approvalKeys.all }),
  });
}

export function useApproveAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/approvals/approve-all'),
    onSettled: () => qc.invalidateQueries({ queryKey: approvalKeys.all }),
  });
}

export function useRejectAll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/api/approvals/reject-all'),
    onSettled: () => qc.invalidateQueries({ queryKey: approvalKeys.all }),
  });
}
